import DeployButton from './DeployButton';
import { useSelector } from 'react-redux';
import {
  selectDefaultCreationEnvironment,
  selectProductionCrmOrg,
  selectCrmOrgs,
} from '../../pages/environments/environmentsReducer';
import useCheckManagedPackage from '../install-managed-package/useCheckManagedPackage';
import usePaywall from '../../dashboard/paywall/usePaywall';
import { useState } from 'react';
import AutomationDeployPaywallBlock from './AutomationDeployPaywallBlock';
import { CompleteSfSetupForDeployDialog } from './complete-sf-setup/CompleteSfSetupForDeployDialog';
import { useDeployButtonContext } from './DeployButtonContext';
import { useShouldRequestToDeploy } from './useShouldRequestToDeploy';
import { useIsSetupCompleted } from '../../../hooks/useIsSetupCompleted';
import useUserInfo from '../../../hooks/useUserInfo';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';
import pluralize from 'pluralize';

/**
 * Props for the ConnectedDeployButton component.
 */
export interface ConnectedDeployButtonProps {
  /**
   * Callback function triggered when the deploy or save button is clicked.
   * @param crmOrgIds - An array of CRM organization IDs. If the array is empty, the operation is a save. otherwise, it is a deploy.
   * @returns A promise that resolves when when the save is completed.
   */
  onDeployOrSave: (crmOrgIds: string[]) => Promise<any>;

  /**
   * Specifies whether the button is disabled.
   */
  disabled?: boolean;

  /**
   * Callback function triggered when the diff is shown.
   */
  onShowDiff?: () => void;

  /**
   * Specifies whether the button is associated with a paywall.
   */
  isWithPaywall: boolean;
}

const ConnectedDeployButton = ({
  onDeployOrSave,
  disabled,
  onShowDiff,
  isWithPaywall,
}: ConnectedDeployButtonProps) => {
  const { addNotification } = useSweepNotifications();
  const { checkIfUserHasPermissionsForOrg } = useUserInfo();
  const { entityNamePlural, entityName, permissions } = useDeployButtonContext();
  const productionEnvironment = useSelector(selectProductionCrmOrg);
  const defaultCreationEnvironment = useSelector(selectDefaultCreationEnvironment);
  const { getIsManagedPackagedInstalledWithRecheck } = useCheckManagedPackage();
  const { isPaywallActive } = usePaywall();
  const [isPaywallDialogOpened, setIsPaywallDialogOpened] = useState(false);
  const [completeSfSetupDialog, setCompleteSfSetupDialog] = useState<{
    isOpen: boolean;
    crmOrgIdsToDeploy: string[];
  }>();
  const canDeploy = useIsSetupCompleted();
  const crmOrgs = useSelector(selectCrmOrgs);

  const shouldRequestToDeploy = useShouldRequestToDeploy();

  const deployOrShowDialog = async (crmOrgIds: string[]) => {
    if (isWithPaywall && isPaywallActive) {
      setIsPaywallDialogOpened(true);
      return;
    }
    await getIsManagedPackagedInstalledWithRecheck({
      withPermissionsCheck: true,
    });

    if (canDeploy) {
      const allCrmOrgsWithPermissions = await Promise.all(
        crmOrgIds.map((id) => checkIfUserHasPermissionsForOrg(id, permissions.deploy)),
      );
      const orgsWithoutPermissions = crmOrgIds.filter(
        (orgId, idx) => !allCrmOrgsWithPermissions[idx],
      );

      if (!shouldRequestToDeploy && orgsWithoutPermissions.length > 0) {
        const _crmOrgs = crmOrgs
          .filter((crmOrg) => orgsWithoutPermissions.includes(crmOrg.id))
          ?.map((crmOrg) => crmOrg.name);
        const crmOrgNames = _crmOrgs.join(', ');

        addNotification({
          message: `You don't have permission to deploy ${entityNamePlural} to ${pluralize('environment', _crmOrgs?.length)} ${crmOrgNames}`,
          variant: SweepNotificationVariant.Error,
        });
        return;
      }
      await onDeployOrSave(crmOrgIds);
    } else {
      setCompleteSfSetupDialog({
        isOpen: true,
        crmOrgIdsToDeploy: crmOrgIds,
      });
    }
  };

  return (
    <>
      {isPaywallDialogOpened && (
        <AutomationDeployPaywallBlock
          closeDialog={() => setIsPaywallDialogOpened(false)}
          entityNamePlural={entityNamePlural}
        />
      )}
      <DeployButton
        isRequestToDeploy={shouldRequestToDeploy}
        onShowDiff={onShowDiff}
        defaultCreationEnvironmentName={defaultCreationEnvironment?.name}
        prodEnvironmentName={productionEnvironment?.name}
        defaultIsProd={defaultCreationEnvironment?.id === productionEnvironment?.id}
        onDeployDefaultClick={
          defaultCreationEnvironment
            ? async () => await deployOrShowDialog([defaultCreationEnvironment.id])
            : undefined
        }
        onDeployBothClick={
          defaultCreationEnvironment && productionEnvironment
            ? async () =>
                await deployOrShowDialog([defaultCreationEnvironment.id, productionEnvironment.id])
            : undefined
        }
        onSaveClick={async () => await onDeployOrSave([])}
        disabled={disabled}
      />
      {completeSfSetupDialog?.isOpen && (
        <CompleteSfSetupForDeployDialog
          cancelDialog={async () => {
            setCompleteSfSetupDialog(undefined);
          }}
          entityName={entityName}
          onCompleteItemsCallback={async () => {
            onDeployOrSave(completeSfSetupDialog.crmOrgIdsToDeploy);
            setCompleteSfSetupDialog(undefined);
          }}
          title={`Your ${entityName} was saved 🎉`}
        />
      )}
    </>
  );
};

export default ConnectedDeployButton;
